import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getCurrentUserId, getAccessToken } from "../../awsAuth";

import "../Feed/Feed.scss";
import "./Saved.scss";

import DynamicHeader from "../Header/DynamicHeader";
import SelectedCollection from "./SelectedCollection";
import CreateCollection from "./CreateCollection";

import plusIcon from "./img/plus.png";

import moreIcon from "./img/more.png";
import renameIcon from "./img/rename.png";
import deleteIcon from "./img/delete.png";
import okIcon from "./img/ok.png";

const API = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const DEFAULT_COLLECTION_ID = "all";

const ASSET_ORIGIN = "https://cvanto-photos.s3.us-east-1.amazonaws.com";
const normalizeUrl = (src) => {
  if (!src || typeof src !== "string") return null;
  const s = src.trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^s3:\/\//i.test(s)) {
    const m = s.match(/^s3:\/\/([^/]+)\/(.+)$/i);
    if (m) return `https://${m[1]}.s3.amazonaws.com/${m[2]}`;
  }
  return `${ASSET_ORIGIN}/${s.replace(/^\/+/, "")}`;
};

const getThumbSmart = (it) => {
  if (!it || typeof it !== "object") return null;
  const directString = [
    "cover",
    "image",
    "thumbnail",
    "preview",
    "photo",
    "picture",
    "mainImage",
    "mainPhoto",
    "coverUrl",
    "imageUrl",
  ]
    .map((k) => it?.[k])
    .find((v) => typeof v === "string" && v);
  if (directString) return normalizeUrl(directString);

  const directObjUrl = [
    "cover",
    "image",
    "thumbnail",
    "preview",
    "photo",
    "picture",
    "mainImage",
    "mainPhoto",
  ]
    .map((k) => it?.[k]?.url)
    .find((v) => typeof v === "string" && v);
  if (directObjUrl) return normalizeUrl(directObjUrl);

  const arrayStringFields = ["images", "photos", "media", "gallery", "attachments"];
  for (const f of arrayStringFields) {
    const arr = it?.[f];
    if (Array.isArray(arr) && arr.length) {
      const firstStr = arr.find((x) => typeof x === "string" && x);
      if (firstStr) return normalizeUrl(firstStr);
      const firstObjUrl = arr.map((x) => x?.url).find((u) => typeof u === "string" && u);
      if (firstObjUrl) return normalizeUrl(firstObjUrl);
    }
  }

  const postCandidates = [it?.content?.images, it?.content?.photos, it?.content?.media, it?.assets].filter(
    Boolean
  );
  for (const cand of postCandidates) {
    if (Array.isArray(cand) && cand.length) {
      const str = cand.find((x) => typeof x === "string" && x);
      if (str) return normalizeUrl(str);
      const obj = cand.map((x) => x?.url).find((u) => typeof u === "string" && u);
      if (obj) return normalizeUrl(obj);
    }
  }

  const misc =
    it?.firstImage ||
    it?.firstPhoto ||
    it?.primaryImage ||
    it?.primaryPhoto ||
    it?.images?.[0]?.src ||
    it?.photos?.[0]?.src;

  if (typeof misc === "string") return normalizeUrl(misc);
  if (typeof misc?.url === "string") return normalizeUrl(misc.url);
  return null;
};

const fetchListingThumbsBatch = async (ids = []) => {
  const uniq = Array.from(new Set(ids.filter(Boolean)));
  if (uniq.length === 0) return {};
  const url = `${API}/api/listings/thumbs?ids=${encodeURIComponent(uniq.join(","))}`;
  const r = await axios.get(url, { validateStatus: () => true });
  const out = {};
  if (r.status >= 200 && r.status < 300 && Array.isArray(r.data?.items)) {
    for (const row of r.data.items) {
      const item = row?.item || null;
      out[row.id] = item ? getThumbSmart(item) : null;
    }
  }
  return out;
};

const fetchPostMeta = async (id) => {
  try {
    const r = await axios.get(`${API}/api/posts/${id}`, { validateStatus: () => true });
    if (r.status >= 200 && r.status < 300) return r.data || null;
  } catch {}
  return null;
};

const SAVED_CACHE_TTL = 3 * 60 * 1000;

const getSavedCacheKey = (userId) =>
  userId ? `cv:savedCols:${String(userId).trim()}` : null;

const readSavedCache = (userId) => {
  try {
    if (typeof window === "undefined") return null;
    const key = getSavedCacheKey(userId);
    if (!key) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.ts || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > SAVED_CACHE_TTL) return null;
    return {
      collections: Array.isArray(parsed.collections) ? parsed.collections : [],
      colPreviews: parsed.colPreviews && typeof parsed.colPreviews === "object"
        ? parsed.colPreviews
        : {},
    };
  } catch {
    return null;
  }
};

const writeSavedCache = (userId, collections, colPreviews) => {
  try {
    if (typeof window === "undefined") return;
    const key = getSavedCacheKey(userId);
    if (!key) return;
    const payload = {
      ts: Date.now(),
      collections,
      colPreviews,
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
  }
};

const Saved = () => {
  const navigate = useNavigate();
  const userId = getCurrentUserId?.();

  useEffect(() => {
    if (!userId) {
      navigate("/auth", { replace: true, state: { from: "/saved" } });
    }
  }, [userId, navigate]);

  const initialCached = readSavedCache(userId);

  const [collections, setCollections] = useState(
    initialCached?.collections?.length
      ? initialCached.collections
      : [{ collectionId: DEFAULT_COLLECTION_ID, name: "Усі" }]
  );
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [colPreviews, setColPreviews] = useState(initialCached?.colPreviews || {});
  const [view, setView] = useState("collections");
  const [activeCollectionId, setActiveCollectionId] = useState(DEFAULT_COLLECTION_ID);

  const [createOpen, setCreateOpen] = useState(false);

  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [menuClosingFor, setMenuClosingFor] = useState(null);
  const [confirmDeleteFor, setConfirmDeleteFor] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [menuSide, setMenuSide] = useState("right");

  const refreshCollections = useCallback(
    async ({ force = false } = {}) => {
      if (!userId) return;

      setCollectionsLoading(true);

      try {
        if (!force) {
          const cached = readSavedCache(userId);
          if (cached && cached.collections?.length) {
            setCollections(cached.collections);
            setColPreviews(cached.colPreviews || {});
            setCollectionsLoading(false);
            return;
          }
        }

        const authToken = await getAccessToken().catch(() => null);
        const authHeaders = { Authorization: `Bearer ${authToken}` };

        const res = await axios.get(`${API}/api/saved/collections/${userId}`, { headers: authHeaders });
        let cols = Array.isArray(res.data) ? res.data : [];
        if (!cols.find((c) => c.collectionId === DEFAULT_COLLECTION_ID)) {
          cols.unshift({ collectionId: DEFAULT_COLLECTION_ID, name: "Усі" });
        }
        setCollections(cols);

        const previews = {};
        await Promise.all(
          cols.map(async (c) => {
            try {
              const url =
                c.collectionId === DEFAULT_COLLECTION_ID
                  ? `${API}/api/saved/${userId}?limit=4`
                  : `${API}/api/saved/${userId}?collectionId=${encodeURIComponent(
                      c.collectionId
                    )}&limit=4`;

              const savedRes = await axios.get(url, { headers: authHeaders, validateStatus: () => true });
              const saved = Array.isArray(savedRes.data) ? savedRes.data : [];

              const listings = [];
              const posts = [];
              for (const s of saved.slice(0, 4)) {
                if ((s.type || "listing") === "post") posts.push(s.postId);
                else listings.push(s.postId);
              }

              const listingThumbsMap = await fetchListingThumbsBatch(listings);

              const postThumbs = [];
              for (const pid of posts) {
                const meta = await fetchPostMeta(pid);
                const th = meta ? getThumbSmart(meta) : null;
                if (th) postThumbs.push(th);
              }

              const thumbs = [];
              for (const s of saved.slice(0, 4)) {
                if ((s.type || "listing") === "post") {
                  if (postThumbs.length) thumbs.push(postThumbs.shift());
                  else thumbs.push(null);
                } else {
                  const lid = s.postId;
                  thumbs.push(listingThumbsMap[lid] || null);
                }
              }

              previews[c.collectionId] = thumbs.filter(Boolean);
            } catch {
              previews[c.collectionId] = [];
            }
          })
        );

        setColPreviews(previews);

        writeSavedCache(userId, cols, previews);
      } finally {
        setCollectionsLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    refreshCollections({ force: false });
  }, [refreshCollections]);

  const openCollection = (collectionId) => {
    setActiveCollectionId(collectionId || DEFAULT_COLLECTION_ID);
    setView("collection");
  };

  const handleCreated = async () => {
    await refreshCollections({ force: true });
    setCreateOpen(false);
  };

  const apiDeleteCollection = async (collectionId) => {
    if (collectionId === DEFAULT_COLLECTION_ID) return;
    if (!userId) return;
    const token = await getAccessToken().catch(() => null);
    await axios.delete(`${API}/api/saved/collections/${collectionId}`, {
      params: { userId },
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const deleteCollection = async (collectionId) => {
    try {
      await apiDeleteCollection(collectionId);
      setMenuOpenFor(null);
      setMenuClosingFor(null);
      setConfirmDeleteFor(null);
      if (activeCollectionId === collectionId) {
        setView("collections");
        setActiveCollectionId(DEFAULT_COLLECTION_ID);
      }
      await refreshCollections({ force: true });
    } catch {
      alert("Не вдалося видалити колекцію");
    }
  };

  const submitRename = async (collectionId) => {
    const newName = (editingName || "").trim();
    if (!newName) {
      setEditingId(null);
      return;
    }
    try {
      const token = await getAccessToken().catch(() => null);
      await axios.patch(`${API}/api/saved/collections/${collectionId}`, { userId, name: newName }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditingId(null);
      await refreshCollections({ force: true });
    } catch {
      alert("Не вдалося перейменувати");
    }
  };

  const closeMenuSmooth = useCallback((id) => {
    if (!id) return setMenuOpenFor(null);
    setMenuClosingFor(id);
    setTimeout(() => {
      setMenuClosingFor(null);
      setMenuOpenFor(null);
      setConfirmDeleteFor(null);
    }, 180);
  }, []);

  const headerTitle =
    view === "collection"
      ? collections.find((c) => c.collectionId === activeCollectionId)?.name || "Колекція"
      : "Збережене";

  const headerOnBack = view === "collection" ? () => setView("collections") : undefined;

  if (!userId) return null;

  return (
    <>
      {view === "collection" && (
      <DynamicHeader
        title={headerTitle}
        variant="glass"
        size="compact"
        hideOnScroll={true}
        showBack={true}
        onBack={headerOnBack}
      />
    )}

      <div
        className="saved saved--has-local-header"
        onClick={() => {
          if (menuOpenFor) closeMenuSmooth(menuOpenFor);
          setEditingId(null);
        }}
      >
        {view === "collections" && (
          <>
            <div className="savedCols">
              {collectionsLoading ? (
                <div className="savedCols__skeleton">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div className="svCol-card svCol-card--skel" key={`sk-${i}`}>
                      <div className="svCol-preview svCol-preview--single">
                        <div className="svCol-ph svCol-ph--cover" />
                      </div>
                      <div className="svCol-title"> </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="savedCols__grid">
                  {collections.map((c) => {
                    const isAll = c.collectionId === DEFAULT_COLLECTION_ID;
                    const thumbs = colPreviews[c.collectionId] || [];
                    const cover = thumbs[0];
                    const isEditing = editingId === c.collectionId;
                    const isMenuOpen = menuOpenFor === c.collectionId;
                    const isMenuClosing = menuClosingFor === c.collectionId;
                    const isConfirmDelete = confirmDeleteFor === c.collectionId;

                    return (
                      <div
                        key={c.collectionId}
                        className={`svCol-card ${isEditing ? "is-editing" : ""} ${
                          (isMenuOpen || isMenuClosing) ? "svCol-card--menuOpen" : ""
                        }`}
                        onClick={(e) => {
                          if (
                            !(
                              e.target.closest?.(".svCol-actions") ||
                              e.target.closest?.(".svCol-menu") ||
                              e.target.closest?.(".svCol-renameRow")
                            )
                          ) {
                            openCollection(c.collectionId);
                          }
                        }}
                        title={c.name}
                      >
                        {isAll ? (
                          <div className="svCol-preview svCol-preview--all">
                            {thumbs
                              .slice(0, 4)
                              .concat(Array(Math.max(0, 4 - thumbs.length)).fill(null))
                              .map((src, idx) => (
                                <div className="svCol-cell" key={idx}>
                                  {src ? (
                                    <img src={src} alt="" loading="lazy" decoding="async" />
                                  ) : (
                                    <div className="svCol-ph" />
                                  )}
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="svCol-preview svCol-preview--single">
                            {cover ? (
                              <img
                                className="svCol-cover"
                                src={cover}
                                alt=""
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <div className="svCol-ph svCol-ph--cover" />
                            )}
                          </div>
                        )}

                        <div
                          className={`svCol-title ${
                            isEditing ? "svCol-title--editing" : ""
                          }`}
                        >
                          {!isEditing ? (
                            <span>{c.name}</span>
                          ) : (
                            <div
                              className="svCol-renameRow"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                className="svCol-renameInput"
                                defaultValue={c.name}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") submitRename(c.collectionId);
                                  if (e.key === "Escape") {
                                    setEditingId(null);
                                  }
                                }}
                                autoFocus
                                inputMode="text"
                              />
                              <button
                                className="svCol-okBtn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  submitRename(c.collectionId);
                                }}
                                title="Підтвердити"
                                aria-label="Підтвердити перейменування"
                              >
                                <img src={okIcon} alt="" />
                              </button>
                            </div>
                          )}
                        </div>

                        {!isAll && (
                          <div className="svCol-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="svCol-moreBtn"
                              onClick={(e) => {
                                setEditingId(null);
                                if (isMenuOpen) {
                                  closeMenuSmooth(c.collectionId);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const viewportWidth =
                                    window.innerWidth ||
                                    document.documentElement.clientWidth ||
                                    0;
                                  const approxMenuWidth = 220;
                                  const spaceLeft = rect.left;
                                  const spaceRight = viewportWidth - rect.right;
                                  const side =
                                    spaceRight >= spaceLeft ? "left" : "right";
                                  setMenuSide(side);
                                  setMenuOpenFor(c.collectionId);
                                  setConfirmDeleteFor(null);
                                }
                              }}
                              title="Дії"
                              aria-label="Дії з колекцією"
                            >
                              <img src={moreIcon} alt="" />
                            </button>

                            {(isMenuOpen || isMenuClosing) && (
                              <div
                                className={`svCol-menu svCol-menu--${menuSide} ${
                                  isMenuOpen ? "menu-in" : ""
                                } ${isMenuClosing ? "menu-out" : ""}`}
                                role="menu"
                              >
                                {!isConfirmDelete ? (
                                  <>
                                    <button
                                      className="svCol-menuItem svCol-menuItem--stagger1"
                                      onClick={() => {
                                        closeMenuSmooth(c.collectionId);
                                        setEditingId(c.collectionId);
                                        setEditingName(c.name || "");
                                      }}
                                    >
                                      <img
                                        className="svCol-icon svCol-icon--noInvert"
                                        src={renameIcon}
                                        alt=""
                                      />
                                      <span>Перейменувати</span>
                                    </button>
                                    <button
                                      className="svCol-menuItem svCol-menuItem--stagger2"
                                      onClick={() =>
                                        setConfirmDeleteFor(c.collectionId)
                                      }
                                    >
                                      <img
                                        className="svCol-icon svCol-icon--noInvert"
                                        src={deleteIcon}
                                        alt=""
                                      />
                                      <span>Видалити</span>
                                    </button>
                                  </>
                                ) : (
                                  <div className="svCol-confirm">
                                    <div className="svCol-confirmText">
                                      Точно видалити?
                                    </div>
                                    <div className="svCol-confirmRow">
                                      <button
                                        className="svCol-confirmBtn svCol-confirmBtn--ghost"
                                        onClick={() => setConfirmDeleteFor(null)}
                                      >
                                        Ні
                                      </button>
                                      <button
                                        className="svCol-confirmBtn svCol-confirmBtn--primary"
                                        onClick={() => deleteCollection(c.collectionId)}
                                      >
                                        Так
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              className="saved__fab"
              onClick={() => setCreateOpen(true)}
              title="Нова колекція"
              aria-label="Додати колекцію"
            >
              <img
                className="saved__fabIcon saved__fabIcon--autoInvert"
                src={plusIcon}
                alt=""
              />
            </button>
          </>
        )}

        {view === "collection" && (
          <SelectedCollection
            API={API}
            userId={userId}
            collectionId={activeCollectionId}
            onBack={() => setView("collections")}
            onDeleteCollection={deleteCollection}
            refreshCollections={refreshCollections}
          />
        )}
      </div>

      <CreateCollection
        open={createOpen}
        userId={userId}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
};

export default Saved;
